import { DataAccess, HttpRequestOptions } from "../../../dataaccess/data.access";
import { Cache } from "../../../decorators/cache.decorator";
import { Item } from "../../../pojo/Item";
import { Proto } from "../../../pojo/Proto";
import { Blackout, BlackoutDate, BlackoutRoom, SpaceService } from "../../../services/space.service";
import { jSith } from "../../../util/jquery-replacement";
import { S25Util } from "../../../util/s25-util";
import { OptBean } from "../../s25-opt/s25.opt.component";
import { OptUtil } from "../../s25-opt/s25.opt.util";
import { CalendarData } from "./CalendarData";

const NON_BLACKOUT_PARAMS: readonly string[] = [
    "obj_cache_accl",
    "mode",
    "comptype",
    "compsubject",
    "state",
    "reservations_state",
    "sort",
    "include_related_events",
];
const MAX_BLACKOUT_SUBJECTS = 3;

export class CalendarService {
    /**
     * Mutates rootData to add blackout information to events that fall within blackout date ranges.
     * TODO: return new objects instead of modifying these in place.
     */
    public static setBlackouts(
        blackouts: { blackout?: Blackout[] },
        rootData: CalendarData,
        truncated: boolean = false,
    ) {
        const blackoutDateHash: { [hash: string]: true } = {};
        blackouts?.blackout?.reverse();

        jSith.forEach(blackouts?.blackout, (_, blackout: Blackout) => {
            blackout.date?.reverse();
            jSith.forEach(blackout.date, (_, blackoutDate: BlackoutDate) => {
                jSith.forEach(
                    S25Util.array
                        .forceArray<CalendarData.DayOfEvents>(S25Util.propertyGet(rootData, "events"))
                        .filter(S25Util.isDefined),
                    (_, dayOfEvents: CalendarData.DayOfEvents) => {
                        if (
                            S25Util.date.isBetweenEqual(
                                dayOfEvents.date,
                                S25Util.date.getDate(blackoutDate.start_dt),
                                S25Util.date.toEndOfDay(blackoutDate.end_dt),
                            )
                        ) {
                            const eventKeyHash = dayOfEvents.date + "&" + this.blackoutKey(blackout);
                            if (!blackoutDateHash[eventKeyHash]) {
                                blackoutDateHash[eventKeyHash] = true;
                                dayOfEvents.rsrv = [
                                    {
                                        event_name: blackout.profile_name, //remove blackout prefix ANG-4021
                                        rsrv_start_dt: blackoutDate.start_dt,
                                        rsrv_end_dt: blackoutDate.end_dt,
                                        subject: blackout.room
                                            .map(
                                                (room: BlackoutRoom): CalendarData.Subject => ({
                                                    itemId: room.room_id,
                                                    itemName: room.room_short,
                                                    itemTypeId: 4,
                                                    has_more:
                                                        truncated && blackout.room.length > MAX_BLACKOUT_SUBJECTS
                                                            ? 1
                                                            : 0,
                                                }),
                                            )
                                            .filter((_, i: number) => !truncated || i < MAX_BLACKOUT_SUBJECTS),
                                    } satisfies CalendarData.Reservation,
                                    ...(dayOfEvents.rsrv || []),
                                ];
                            }
                        }
                    },
                );
            });
        });
    }

    public static getBlackouts(
        params: CalendarService.BlackoutParams,
        dateOverride?: number | string | Date,
    ): Promise<{
        blackout?: Blackout[];
    }> {
        const processedParams = CalendarService.defaultParams(params, dateOverride)
            .split(/[?&]/)
            .filter((arg, i) => {
                // skip the path, which is (obviously) always first
                if (i === 0) {
                    return false;
                }
                return !NON_BLACKOUT_PARAMS.some((blockedParam) => arg.includes(blockedParam + "="));
            })
            .map((arg: string) => arg.replace("spaces_", ""));
        return SpaceService.getBlackouts("?" + processedParams.join("&"));
    }

    public static defaultParams(
        params: CalendarService.DataParams | CalendarService.BlackoutParams,
        dateOverride?: number | string | Date,
    ) {
        const startDate = this.getDate(dateOverride, params);
        const endDate = this.getDate(dateOverride, params, true);
        if (params.modelBean.optBean.multiQueryStr) {
            params.modelBean.searchQuery = S25Util.getMultiQuery(
                params.modelBean.searchQuery,
                params.modelBean.optBean.multiQueryStr,
            );
        }

        if (params.modelBean.searchQuery) {
            //calendar uses reservations.xml, which has its own state so we need to explicitly use events_state as that is what search queries are referring to...
            params.modelBean.searchQuery = params.modelBean.searchQuery.replace("&state=", "&events_state=");
        }

        return (
            "/home/calendar/calendardata.json?mode=pro&obj_cache_accl=" +
            (params.modelBean.optBean.useCache === false ? "force" : "0") +
            "&start_dt=" +
            startDate +
            "&end_dt=" +
            endDate +
            "&comptype=" +
            params.comptype +
            "&sort=evdates_event_name" +
            "&compsubject=" +
            params.modelBean.optBean.compsubject +
            (params.modelBean.searchQuery ? params.modelBean.searchQuery : "") +
            (params.modelBean.optBean.includeAddtl ? "&include_addtl_time=T" : "") +
            (params.modelBean.optBean.showRelatedEvents ? "&include_related_events=T" : "") +
            (params.modelBean.optBean.compsubject === "location" && params.modelBean.optBean.showRelatedLocations
                ? "&include=related"
                : "") +
            (["event", "organization", "location", "resource"].indexOf(params.modelBean.optBean.compsubject) > -1
                ? !params.modelBean.searchQuery || params.modelBean.searchQuery.indexOf("&state=") === -1
                    ? "&state=0+1+3+4+99"
                    : ""
                : !params.modelBean.searchQuery || params.modelBean.searchQuery.indexOf("&reservations_state=") === -1
                  ? "&reservations_state=0+1+3+4+99"
                  : "")
        );
    }

    public static hasMoreParams(params: CalendarService.HasMoreParams, dateOverride?: number | string | Date) {
        const startDate = this.getDate(dateOverride, params);
        const endDate = this.getDate(dateOverride, params, true);
        return (
            "/home/calendar/calhasmore.json?mode=pro&start_dt=" +
            startDate +
            "&end_dt=" +
            endDate +
            "&obj_cache_accl=" +
            params.modelBean.data.root.root.obj_id +
            "&compsubject=" +
            params.modelBean.optBean.compsubject
        );
    }

    public static angularParams(params: CalendarService.DataParams) {
        if (params.comptype === "home" && params.modelBean.optBean.chosen) {
            return "&" + params.modelBean.optBean.chosen.obj.val;
        } else {
            return "";
        }
    }

    public static getData(
        params: CalendarService.DataParams,
        options?: HttpRequestOptions,
    ): Promise<{ root: CalendarData }> {
        return DataAccess.get(
            DataAccess.injectCaller(
                CalendarService.defaultParams(params) + CalendarService.angularParams(params),
                "CalendarService.getData",
            ),
            options,
        );
    }

    public static getCalendarDayPage({
        objId,
        date,
        comptype,
        compsubject,
        lastId,
        includeAddtl,
    }: {
        objId: string;
        date: Date;
        comptype: OptUtil.CompType;
        compsubject: Item.Subject;
        lastId: string;
        includeAddtl: string;
    }): Promise<{ root: CalendarData }> {
        let url =
            objId &&
            "/home/calendar/calendardata.json?mode=pro&obj_cache_accl=" +
                objId +
                "&start_dt=" +
                S25Util.date.toS25ISODateStr(date) +
                "&end_dt=" +
                S25Util.date.toS25ISODateStr(date) +
                "&page=1" +
                "&comptype=" +
                comptype +
                (includeAddtl ? "&include_addtl_time=T" : "") +
                "&sort=evdates_event_name" +
                "&compsubject=" +
                compsubject +
                "&last_id=" +
                lastId;
        return CalendarService.getCalendarDayPageDao(url);
    }

    //Sepreate DAO for easier mocking
    public static getCalendarDayPageDao(url: string) {
        return DataAccess.get(DataAccess.injectCaller(url, "CalendarService.getCalendarDayPage"));
    }

    @Cache({ immutable: true, targetName: "CalendarService" })
    public static getHasMore(params: CalendarService.HasMoreParams): Promise<{ hasMore: number[] }> {
        return DataAccess.get(
            DataAccess.injectCaller(CalendarService.hasMoreParams(params), "CalendarService.getHasMore"),
        );
    }

    public static getCalChoose() {
        return DataAccess.get(DataAccess.injectCaller("/home/calendar/calchoose.json", "CalendarService.getCalChoose"));
    }

    public static scopeToDataRequest(scope: any) {
        return {
            comptype: scope.comptype,
            modelBean: {
                searchQuery: scope.modelBean.searchQuery,
                data: scope.modelBean.data,
                optBean: {
                    datePropertyValue: scope.modelBean.optBean.datePropertyValue,
                    date: scope.modelBean.optBean.date,
                    endDate: scope.modelBean.optBean.endDate,
                    useCache: scope.modelBean.optBean.useCache,
                    compsubject: scope.modelBean.optBean.compsubject,
                    chosen: scope.modelBean.optBean.chosen,
                    useServiceCache: scope.modelBean.optBean.useServiceCache,
                    showRelatedEvents: scope.modelBean.optBean.showRelatedEvents,
                    showRelatedLocations: scope.modelBean.optBean.showRelatedLocations,
                    includeAddtl: scope.modelBean.optBean.includeAddtl,
                    multiQueryStr: scope.modelBean.optBean.multiQueryStr,
                },
            },
            weekstart: scope.weekstart,
        };
    }

    public static generateCalendar(
        startDt: number | string | Date,
        endDt: number | string | Date,
        weekstart: number,
        comptype: OptUtil.CompType,
        rsrvData: { [key: Proto.ISODateString]: CalendarData.Reservation[] },
    ): { root: CalendarData } {
        var daysArr = new Array(S25Util.date.diffDays(startDt, endDt) + 1);
        var daysPerRow = 7;
        const ret = {
            root: {
                comptype: comptype,
                days_per_row: daysPerRow,
                lastupdate: S25Util.date.toS25ISODateTimeStr(new Date()),
                events: daysArr,
                weekday: new Array(daysPerRow),
            } satisfies CalendarData,
        };
        let firstDayOfWeek = daysPerRow === 7 ? S25Util.date.firstDayOfWeek(new Date(), weekstart) : startDt;
        let i, iDate;
        for (i = 0; i < ret.root.days_per_row; i++) {
            iDate = S25Util.date.addDays(firstDayOfWeek, i);
            ret.root.weekday[i] = S25Util.date.getDayName(iDate.getDay());
        }
        for (i = 0; i < ret.root.events.length; i++) {
            iDate = S25Util.date.addDays(startDt, i);
            var iDateStr = S25Util.date.toS25ISODateTimeStr(iDate);
            ret.root.events[i] = {
                is_start: i === 0 ? 1 : null,
                date: iDateStr,
                day: iDate.getDate(),
                month_name: i === 0 || iDate.getDate() === 1 ? S25Util.date.getMonthName(iDate) : null,
                hasMore: 0,
                rsrv: rsrvData[iDateStr],
            };
        }
        return ret;
    }

    public static getCacheParams(params: any, dateOverride: any) {
        return {
            query_id: params.modelBean.query_id,
            compsubject: params.modelBean.optBean.compsubject,
            start_dt: this.getDate(dateOverride, params),
            end_dt: this.getDate(dateOverride, params, true),
            include:
                params.modelBean.optBean.compsubject === "location" &&
                params.modelBean.optBean.showRelatedLocations &&
                "related",
            include_related_events: params.modelBean.optBean.showRelatedEvents && "T",
            include_addtl_time: params.modelBean.optBean.includeAddtl && "T",
            state:
                ["event", "organization", "location", "resource"].indexOf(params.modelBean.optBean.compsubject) > -1 &&
                (!params.modelBean.searchQuery || params.modelBean.searchQuery.indexOf("&state=") === -1) &&
                "0+1+3+4+99",
            reservations_state:
                ["event", "organization", "location", "resource"].indexOf(params.modelBean.optBean.compsubject) ===
                    -1 &&
                (!params.modelBean.searchQuery ||
                    params.modelBean.searchQuery.indexOf("&reservations_state=") === -1) &&
                "0+1+3+4+99",
        };
    }

    public static getDate(
        dateOverride: number | string | Date,
        params: CalendarService.DataParams | CalendarService.HasMoreParams | CalendarService.BlackoutParams,
        isEndDate?: boolean,
    ) {
        return S25Util.date.toS25ISODateStr(
            dateOverride ||
                (!isEndDate
                    ? S25Util.date.firstDayOfWeek(params.modelBean.optBean.date, params.weekstart)
                    : S25Util.date.lastDayOfWeek(
                          params.modelBean.optBean.endDate || params.modelBean.optBean.date,
                          params.weekstart,
                      )),
        );
    }

    private static blackoutKey(blackout: Blackout) {
        return blackout.init_start_dt + "&" + blackout.init_end_dt + "&" + blackout.profile_name;
    }
}

export namespace CalendarService {
    export interface DataParams {
        comptype: OptUtil.CompType;
        weekstart: number | undefined;
        modelBean: {
            optBean: Pick<
                OptBean,
                | "multiQueryStr"
                | "useCache"
                | "compsubject"
                | "includeAddtl"
                | "showRelatedEvents"
                | "showRelatedLocations"
                | "chosen"
            > & {
                // override OptBean to accept more loose date types
                date: string | number | Date | undefined;
                endDate: string | number | Date | undefined;
            };
            searchQuery: string | undefined;
        };
    }

    export interface BlackoutParams {
        comptype: OptUtil.CompType;
        weekstart: number | undefined;
        modelBean: {
            optBean: Pick<
                OptBean,
                | "multiQueryStr"
                | "useCache"
                | "compsubject"
                | "includeAddtl"
                | "showRelatedEvents"
                | "showRelatedLocations"
                | "date"
                | "endDate"
            >;
            searchQuery: string | undefined;
        };
    }

    export interface HasMoreParams {
        weekstart: number | undefined;
        modelBean: {
            optBean: Pick<OptBean, "compsubject" | "date" | "endDate">;
            data: { root: { root: { obj_id: number } } };
        };
    }
}
